import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { categoryApi, productApi } from '@/api/endpoints';
import type { ProductSearchParams } from '@/api/types';
import ProductCard from '@/components/product/ProductCard';
import Pagination from '@/components/common/Pagination';
import { Empty, ErrorState, Loading } from '@/components/common/State';

const SORT_OPTIONS = [
  { value: 'newest', label: '최신순' },
  { value: 'popular', label: '판매량순' },
  { value: 'priceAsc', label: '낮은 가격순' },
  { value: 'priceDesc', label: '높은 가격순' },
  { value: 'rating', label: '평점순' },
] as const;

export default function ProductListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params: ProductSearchParams = {
    page: Number(searchParams.get('page') ?? 1),
    size: 20,
    keyword: searchParams.get('keyword') ?? undefined,
    categoryId: searchParams.get('categoryId') ? Number(searchParams.get('categoryId')) : undefined,
    featured: searchParams.get('featured') === 'true' ? true : undefined,
    inStockOnly: searchParams.get('inStockOnly') === 'true' ? true : undefined,
    sortBy: (searchParams.get('sortBy') as ProductSearchParams['sortBy']) ?? 'newest',
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['products', params],
    queryFn: () => productApi.list(params),
  });

  const categories = useQuery({ queryKey: ['categories'], queryFn: categoryApi.tree });

  const patchParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    // 필터가 바뀌면 첫 페이지부터 다시 본다.
    if (!('page' in patch)) next.delete('page');
    setSearchParams(next);
  };

  return (
    <>
      <h1 className="panel-title">전체 상품</h1>

      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <input
          className="input"
          style={{ maxWidth: 240 }}
          placeholder="상품명 검색"
          defaultValue={params.keyword ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') patchParams({ keyword: e.currentTarget.value });
          }}
        />

        <select
          className="select"
          style={{ maxWidth: 180 }}
          value={params.categoryId ?? ''}
          onChange={(e) => patchParams({ categoryId: e.target.value || undefined })}
        >
          <option value="">전체 카테고리</option>
          {categories.data?.flatMap((parent) => [
            <option key={parent.categoryId} value={parent.categoryId}>
              {parent.name}
            </option>,
            ...parent.children.map((child) => (
              <option key={child.categoryId} value={child.categoryId}>
                &nbsp;&nbsp;└ {child.name}
              </option>
            )),
          ])}
        </select>

        <select
          className="select"
          style={{ maxWidth: 160 }}
          value={params.sortBy}
          onChange={(e) => patchParams({ sortBy: e.target.value })}
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={Boolean(params.inStockOnly)}
            onChange={(e) => patchParams({ inStockOnly: e.target.checked ? 'true' : undefined })}
          />
          품절 제외
        </label>
      </div>

      {isLoading && <Loading />}
      {isError && <ErrorState error={error} onRetry={refetch} />}

      {data && (
        <>
          <p className="muted" style={{ marginBottom: 12 }}>
            총 {data.totalElements}개
          </p>
          {data.content.length === 0 ? (
            <Empty title="조건에 맞는 상품이 없습니다." />
          ) : (
            <div className="product-grid">
              {data.content.map((product) => (
                <ProductCard key={product.productId} product={product} />
              ))}
            </div>
          )}
          <Pagination
            page={data.page}
            totalPages={data.totalPages}
            onChange={(page) => patchParams({ page: String(page) })}
          />
        </>
      )}
    </>
  );
}
